import { useEffect, useRef, type ReactNode } from "react"
import Box from "@mui/material/Box"
import type { Theme } from "@mui/material/styles"
import { Field, Form } from "react-final-form"

import type { SxStyles } from "@/types/util"
import FinalFormTextField from "../atoms/final_form_text_field"
import Loading from "../organism/loading"
import PhenoButton from "../atoms/pheno_button"
import WhiteCard from "./white_card"

const styles = {
  textContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "80%",
    height: "76px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    minHeight: "18.75rem",
    width: "20rem",
  },
  title: (theme: Theme) => ({
    fontSize: theme.variables.fontSizes.title,
    fontWeight: 700,
  }),
  message: {
    color: "rgba(0,0,0,.6)",
    textAlign: "center",
  },
  fieldContainer: {
    width: "50%",
  },
  button: { width: "160px" },
  spanJoin: {
    textDecoration: "underline",
    cursor: "pointer",
  },
} satisfies SxStyles

type FormValues = { single: string }

type Props = {
  title: string
  message: string
  fieldType: string
  fieldLabel: string
  fieldInputProps?: object
  buttonText: string
  inAction: boolean
  validate: (value: string) => string | undefined
  onSubmit: (value: string) => void | Promise<void>
  footer?: ReactNode
}

const AuthForm = (props: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const validate = (values: FormValues) => {
    const message = props.validate(values.single)
    return message ? { single: message } : {}
  }

  const onSubmit = async (values: FormValues) => {
    if (props.inAction) {
      return
    }
    await props.onSubmit(values.single)
  }

  const renderContent = () => {
    return (
      <Form<FormValues>
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit }) => (
          <form
            style={styles.form}
            onSubmit={(e) => {
              e.preventDefault()
              void handleSubmit()
            }}
          >
            <Box sx={styles.textContainer}>
              <Box sx={styles.title}>{props.title}</Box>
              <Box sx={styles.message}>{props.message}</Box>
            </Box>
            <Box sx={styles.fieldContainer}>
              <Field
                name="single"
                type={props.fieldType}
                component={FinalFormTextField}
                label={props.fieldLabel}
                inputRef={inputRef}
                inputProps={props.fieldInputProps}
                variant="standard"
              />
            </Box>
            <PhenoButton type="submit" variant="contained" color="primary" sx={styles.button}>
              {props.buttonText}
            </PhenoButton>
          </form>
        )}
      />
    )
  }

  return (
    <WhiteCard footer={props.footer}>
      <Loading inAction={props.inAction} />
      {renderContent()}
    </WhiteCard>
  )
}

export default AuthForm
